import produce from 'immer';
import React, {useCallback, useMemo} from 'react';

import {assert} from 'Asserts';
import MethodEditor from 'procedures/design/method-config/MethodEditor';
import {MethodUpdater as LegacyMethodUpdater} from 'procedures/design/ProcedureEditor';
import {getDefaultStepCidForPoWMethodCidMap} from 'procedures/design/step-config/stepManagementUtils';
import useSelectedStep from 'procedures/design/utils/useSelectedStep';
import {Method} from 'procedures/ProcedureDomainTypes';
import {ProcedurePageContext} from 'procedures/ProcedurePageContext';
import {isMethodNode, isNodeValidInProcedureFlowchart} from 'procedures/processes/MethodNodeUtils';
import {TaskDefinitionVersionSpec} from 'processes/dao/ProcessFlowchartConfigDAO';
import {
  TaskDefinitionVersionSpecWithTaskSchemaFlowchart,
  TaskNodeDetailsSidepanelContentProps,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {TaskDefinitionVersionSpecWithTaskSchema} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorNodeTypes';
import useOnMount from 'ui-library/hooks/useOnMount';

import MethodSidepanelContext from './MethodSidepanelContext';

type MethodTaskDefinitionVersionSpec = TaskDefinitionVersionSpec<Method>;

type MethodUpdater = (
  updater: (
    currentFlowchart: TaskDefinitionVersionSpecWithTaskSchemaFlowchart
  ) => TaskDefinitionVersionSpecWithTaskSchema<Method>,
  isBlocking: boolean
) => void;

export default function MethodSidepanelEditor(props: TaskNodeDetailsSidepanelContentProps): JSX.Element {
  const {procedureTemplateCollectionOwner} = ProcedurePageContext.useContext();
  // TODO: Awaiting template types on the adapter
  const selectedNodeTaskDefinitionVersionSpec =
    props.selectedNodeTaskDefinitionVersionSpec as MethodTaskDefinitionVersionSpec;
  const taskDefinitionVersion = selectedNodeTaskDefinitionVersionSpec.newProcessTaskDefinitionVersion;
  const {selectedStepCid, onChangeSelectedStep} = useSelectedStep({
    defaultStepCidForMethodCidMap: getDefaultStepCidForPoWMethodCidMap(props.flowchart.nodes),
  });

  useOnMount(() => {
    assert(
      Object.values(props.flowchart.nodes).every((node) => isNodeValidInProcedureFlowchart(node)),
      'MethodSidepanelEditor requires a flowchart of nodes that can be used in Procedures'
    );
  });

  // TODO: Awaiting template types on the adapter
  const onUpdateTaskDefinitionVersion = props.onUpdateTaskDefinitionVersion as MethodUpdater;
  const {applicationData: method} = taskDefinitionVersion;
  assert(method != null, 'MethodSidepanelEditor requires a Method task definition version');

  const onChangeMethod = useCallback(
    (_cid: string, methodUpdater: LegacyMethodUpdater): void => {
      onUpdateTaskDefinitionVersion((currentFlowchart) => {
        const currentNodeSpec = currentFlowchart.nodes[
          selectedNodeTaskDefinitionVersionSpec.clientId
        ] as TaskDefinitionVersionSpecWithTaskSchema<Method>;
        assert(
          currentNodeSpec != null,
          `Node ${selectedNodeTaskDefinitionVersionSpec.clientId} not found in current flowchart`
        );
        const currentMethodData = currentNodeSpec.newProcessTaskDefinitionVersion.applicationData;
        assert(
          currentMethodData != null,
          `Node ${selectedNodeTaskDefinitionVersionSpec.clientId} has no application data`
        );
        const newMethodData = methodUpdater(currentMethodData);
        return produce(currentNodeSpec, (draftVersion) => {
          draftVersion.newProcessTaskDefinitionVersion.applicationData = newMethodData;
        });
      }, false);
    },
    [selectedNodeTaskDefinitionVersionSpec, onUpdateTaskDefinitionVersion]
  );

  // TODO(BNCH-133735): Move this into a hook which will delegate to one of the two versions of saveImmediately
  const customSaveImmediately = useCallback(
    // TODO: This should ideally have the same signature as saveImmediately:
    //    (applyChanges: (oldMethod) => newMethod) => Promise<void>
    //    Waiting for workflows support with cb format of `onUpdateTaskDefinitionVersion`
    (methodData: Method): void => {
      onUpdateTaskDefinitionVersion((currentFlowchart) => {
        const currentNodeSpec = currentFlowchart.nodes[
          selectedNodeTaskDefinitionVersionSpec.clientId
        ] as TaskDefinitionVersionSpecWithTaskSchema<Method>;
        if (!currentNodeSpec) {
          throw new Error(
            `Node ${selectedNodeTaskDefinitionVersionSpec.clientId} not found in current flowchart`
          );
        }
        return produce(currentNodeSpec, (draftVersion) => {
          draftVersion.newProcessTaskDefinitionVersion.applicationData = methodData;
        });
      }, true);
    },
    [selectedNodeTaskDefinitionVersionSpec, onUpdateTaskDefinitionVersion]
  );

  const allMethodNames = Object.values(props.flowchart.nodes)
    .filter((node) => isMethodNode(node))
    .map((node) => node.newProcessTaskDefinitionVersion.applicationData!)
    .map((applicationData) => ({cid: applicationData.cid, name: applicationData.name}));

  const contextValue = useMemo(() => ({saveImmediately: customSaveImmediately}), [customSaveImmediately]);

  return (
    <MethodSidepanelContext.Provider value={contextValue}>
      <MethodEditor
        // Rebuild the method editor if the selected node changes
        key={props.selectedNodeTaskDefinitionVersionSpec.clientId}
        selectedMethod={method}
        selectedStepCid={selectedStepCid}
        onChangeSelectedStep={onChangeSelectedStep}
        onChangeMethod={onChangeMethod}
        // TODO: these 3 props need proper hydration via the workflows platform
        procedureTemplateCollectionId={procedureTemplateCollectionOwner.id}
        isProcedureReadOnly={false}
        isProcedureArchived={false}
        allMethodNames={allMethodNames}
      />
    </MethodSidepanelContext.Provider>
  );
}
